import React, {useRef, useEffect, useState, ReactNode, useMemo} from 'react';
import {useFrame, useThree} from '@react-three/fiber';
import * as THREE from 'three';
import {toCanvas} from 'html-to-image';
import {createRoot, Root} from 'react-dom/client';
import {SleepAsync, Timer} from "js-vextensions";
import {vrLayers} from "./VRLayers.js";
import {useXRInputSourceState} from "@react-three/xr";

export type ControllerButtonStates = {trigger: boolean, grip: boolean, stick: boolean, a: boolean, b: boolean};
//export type ControllerButton = keyof ControllerButtonStates;
export type ControllerButton = "trigger" | "grip" | "stick" | "a" | "b";

export const useButtonStateChangeListener = (onButtonStateChanged: (side: "left" | "right", button: ControllerButton, state: "off" | "on")=>void)=>{
	const {gl} = useThree();
	const sideToButtonStates = new Map<string, ControllerButtonStates>();
	useFrame(()=>{
		if (!gl.xr.isPresenting || gl.xr.getSession()?.inputSources == null) return;
		const session = gl.xr.getSession()!;
		for (const inputSource of session.inputSources) {
			const side = inputSource.handedness;
			if (side != "left" && side != "right") continue;
			if (!inputSource.gamepad) continue;

			const prevStates = sideToButtonStates.get(side);
			const states = {
				trigger: inputSource.gamepad.buttons[0].pressed,
				grip: inputSource.gamepad.buttons[1].pressed,
				stick: inputSource.gamepad.buttons[3].pressed,
				a: inputSource.gamepad.buttons[4].pressed,
				b: inputSource.gamepad.buttons[5].pressed,
			};
			if (onButtonStateChanged && prevStates != null) {
				if (states.trigger && !prevStates.trigger) onButtonStateChanged(side, "trigger", states.trigger ? "on" : "off");
				if (states.grip && !prevStates.grip) onButtonStateChanged(side, "grip", states.grip ? "on" : "off");
				if (states.stick && !prevStates.stick) onButtonStateChanged(side, "stick", states.stick ? "on" : "off");
				if (states.a && !prevStates.a) onButtonStateChanged(side, "a", states.a ? "on" : "off");
				if (states.b && !prevStates.b) onButtonStateChanged(side, "b", states.b ? "on" : "off");
			}
			sideToButtonStates.set(side, states);
		}
	});
};

export type OnStickDirectionalStateChanged = (side: "left" | "right", direction: "left" | "right" | "up" | "down", state: "off" | "on")=>void;
export const useStickDirectionalStateChangeListener = (onStickDirectionalStateChanged: OnStickDirectionalStateChanged|n)=>{
	const {gl} = useThree();
	const sideToStickStates = new Map<string, {stickX: number, stickY: number}>();
	useFrame(()=>{
		//const {gl, sideToStickStates, onStickDirectionalStateChanged} = self;
		if (!gl.xr.isPresenting || gl.xr.getSession()?.inputSources == null) return;
		const session = gl.xr.getSession()!;
		for (const inputSource of session.inputSources) {
			const side = inputSource.handedness;
			if (side != "left" && side != "right") continue;
			if (!inputSource.gamepad) continue;

			const stickX = inputSource.gamepad.axes[2] || 0; // X-axis of the stick
			const stickY = inputSource.gamepad.axes[3] || 0; // Y-axis of the stick
			const lastX = sideToStickStates.get(side)?.stickX;
			const lastY = sideToStickStates.get(side)?.stickY;
			const threshold = 0.5;

			if (onStickDirectionalStateChanged && lastX != null && lastY != null) {
				//if (side == "right" && stickY != lastY) console.log("stickY:", stickY, "lastY:", lastY);
				// if x-axis changed from neutral to extended state
				if (Math.abs(stickX) >= threshold && Math.abs(lastX) < threshold) {
					onStickDirectionalStateChanged(side, stickX > 0 ? "right" : "left", "on");
				}
				// if x-axis changed from extended to neutral state
				else if (Math.abs(stickX) < threshold && Math.abs(lastX) >= threshold) {
					onStickDirectionalStateChanged(side, lastX > 0 ? "right" : "left", "off");
				}

				// if y-axis changed from neutral to extended state
				if (Math.abs(stickY) >= threshold && Math.abs(lastY) < threshold) {
					onStickDirectionalStateChanged(side, stickY < 0 ? "up" : "down", "on");
				}
				// if y-axis changed from extended to neutral state
				else if (Math.abs(stickY) < threshold && Math.abs(lastY) >= threshold) {
					onStickDirectionalStateChanged(side, lastY < 0 ? "up" : "down", "off");
				}
			}
			sideToStickStates.set(side, {stickX: stickX, stickY: stickY});
		}
	});
};